import { formatMetric } from "@/lib/format";
import type { LastFlightDetail } from "@/types/api";
import type { WidgetComponentProps } from "../types";
import { useSummaryQuery } from "../useSummaryQuery";

const MPS_TO_KMH = 3.6;

interface Metric {
  label: string;
  value: string;
  ratio: number;
  accent: string;
  hint?: string;
}

function buildMetrics(flight: LastFlightDetail): Metric[] {
  const avgKmh = flight.avg_speed_mps != null ? flight.avg_speed_mps * MPS_TO_KMH : null;
  const maxKmh = flight.max_speed_mps != null ? flight.max_speed_mps * MPS_TO_KMH : null;
  return [
    {
      label: "Smoothness",
      value:
        flight.smoothness_score != null
          ? `${Math.round(flight.smoothness_score)}/100`
          : "—",
      ratio: flight.smoothness_score != null ? flight.smoothness_score / 100 : 0,
      accent: "#4ADE80",
      hint: "higher is better",
    },
    {
      label: "Avg speed",
      value: avgKmh != null ? formatMetric(avgKmh, 1, " km/h") : "—",
      ratio: avgKmh != null ? Math.min(1, avgKmh / 150) : 0,
      accent: "#FF6B1A",
    },
    {
      label: "Peak speed",
      value: maxKmh != null ? formatMetric(maxKmh, 1, " km/h") : "—",
      ratio: maxKmh != null ? Math.min(1, maxKmh / 200) : 0,
      accent: "#FBBF24",
    },
    {
      label: "Max altitude",
      value: formatMetric(flight.max_altitude_m ?? null, 0, " m"),
      ratio:
        flight.max_altitude_m != null ? Math.min(1, flight.max_altitude_m / 500) : 0,
      accent: "#38BDF8",
    },
  ];
}

function RadialGauge({ metric }: { metric: Metric }) {
  const pct = Math.max(0, Math.min(1, metric.ratio));
  const deg = pct * 360;
  return (
    <div className="perf-radial">
      <div
        className="perf-radial__ring"
        style={{
          background: `conic-gradient(${metric.accent} ${deg}deg, rgba(42,52,68,0.6) ${deg}deg)`,
        }}
      >
        <div className="perf-radial__inner">
          <span className="perf-radial__value">{metric.value}</span>
          <span className="perf-radial__label">{metric.label}</span>
        </div>
      </div>
    </div>
  );
}

function TrendBar({ metric }: { metric: Metric }) {
  const pct = Math.max(0, Math.min(1, metric.ratio));
  return (
    <div className="perf-bar">
      <div className="perf-bar__head">
        <span className="perf-bar__label">{metric.label}</span>
        <span className="perf-bar__value">{metric.value}</span>
      </div>
      <div className="perf-bar__track">
        <div
          className="perf-bar__fill"
          style={{ width: `${pct * 100}%`, background: metric.accent }}
        />
      </div>
    </div>
  );
}

export function PerformanceTrendsWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useSummaryQuery();
  if (isLoading) return <p className="widget-empty">Loading…</p>;
  const flight = data?.last_flight;
  if (!flight) return <p className="widget-empty">No flights yet.</p>;

  const metrics = buildMetrics(flight);

  if (sizeVariant === "s") {
    const top = metrics[0];
    return (
      <div className="perf-trends perf-trends--s">
        <RadialGauge metric={top} />
      </div>
    );
  }

  if (sizeVariant === "l") {
    return (
      <div className="perf-trends perf-trends--l">
        <p className="eyebrow">Last flight performance</p>
        <div className="perf-trends__gauges">
          {metrics.slice(0, 4).map((m) => (
            <RadialGauge key={m.label} metric={m} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="perf-trends perf-trends--m">
      {metrics.map((m) => (
        <TrendBar key={m.label} metric={m} />
      ))}
    </div>
  );
}
